import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../layouts/LayoutDefault.jsx";
import axios from "axios";
import MainContainer from "../components/MainContainer.jsx";
import ContentContainer from "../components/ContentContainer.jsx";
import SearchResult from "../components/SearchResult.jsx";

const SearchPage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchString, setSearchString] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams(); // Browsers current Params
  const [enabledSearchBtn, setEnabledSearchBtn] = useState(true);
  const [searchWord, setSearchWord] = useState(""); // Search word for loqic
  const [searchWordResults, setSearchWordResults] = useState(""); // Only to display for the user what the current searchword is
  const [checkboxFilter, setCheckboxFilter] = useState({
    posts: true,
    bands: true,
    tags: true,
  });
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const pageFromUrl = searchParams.get("page");
  const [currentPageNr, setCurrentPageNr] = useState(() => {
    return pageFromUrl ? Number(pageFromUrl) : 1;
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const apiEndpoint = `${apiUrl}search?page=${currentPageNr}${searchString}`;
        const response = await axios({
          method: "GET",
          url: apiEndpoint,
        });
        setData(response.data);
      } catch (error) {
        console.log(
          "error:",
          error.response ? error.response.status : error.message
        );
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data if searchString is not empty or null
    if (searchString && searchString.trim() !== "") {
      fetchData();
    }
  }, [searchString, currentPageNr]);

  // Update the button availability based on search terms
  useEffect(() => {
    if (
      !checkboxFilter.bands &&
      !checkboxFilter.posts &&
      !checkboxFilter.tags
    ) {
      setEnabledSearchBtn(false);
    } else {
      setEnabledSearchBtn(true);
    }
  }, [checkboxFilter]);

  useEffect(() => {
    const pageFromUrl = searchParams.get("page") || 1;
    const queryFromUrl = searchParams.get("q");
    const includeFromUrl = searchParams.get("include");

    // If there are query parameters, set them in the state and trigger the search
    if (queryFromUrl && includeFromUrl) {
      setSearchWord(queryFromUrl); // Set the search word from URL
      setCheckboxFilter({
        posts: includeFromUrl.includes("posts"),
        bands: includeFromUrl.includes("bands"),
        tags: includeFromUrl.includes("tags"),
      });

      // Construct the querystring and initiate the search
      const querystring = `&q=${queryFromUrl}&include=${
        includeFromUrl.includes("bands") ? "bands," : ""
      }${includeFromUrl.includes("posts") ? "posts," : ""}
      ${includeFromUrl.includes("tags") ? "tags" : ""}`;

      setSearchString(querystring); // Set the searchString with URL parameters
      setCurrentPageNr(Number(pageFromUrl)); // Set page from URL
    }
  }, [searchParams]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (searchWord.trim().length < 3) {
      setErrorMsg("Sökning på minst två bokstäver");
      return;
    }
    setCurrentPageNr(1); // Always reset to page 1 when new search.

    // Construct the query string with the cleaned filter
    const querystring = `&q=${searchWord.trim()}&include=${
      checkboxFilter.bands ? "bands," : ""
    }${checkboxFilter.posts ? "posts," : ""}${
      checkboxFilter.tags ? "tags" : ""
    }`;

    setSearchString(querystring);
    navigate(`?page=1${querystring}`);

    // Restore from previous search
    setSearchWordResults(searchWord);
    setErrorMsg("");
    setSearchWord("");
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCheckboxFilter((prevState) => ({
      ...prevState,
      [name]: checked, // update either 'posts' or 'bands'
    }));
  };
  {
    /* Pagination */
  }
  const goToNextPage = () => {
    if (currentPageNr < data.paginator.total_pages) {
      setCurrentPageNr((prevPage) => prevPage + 1);
      // Construct the query string with the cleaned filter
      const querystring = `&q=${searchWord.trim()}&include=${
        checkboxFilter.bands ? "bands," : ""
      }${checkboxFilter.posts ? "posts," : ""}${
        checkboxFilter.tags ? "tags" : ""
      }`;
      setSearchString(querystring);
      navigate(`?page=${currentPageNr + 1}${querystring}`, {
        replace: true,
      });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNr > 1) {
      setCurrentPageNr((prevPage) => prevPage - 1);
      // Construct the query string with the cleaned filter
      const querystring = `&q=${searchWord.trim()}&include=${
        checkboxFilter.bands ? "bands," : ""
      }${checkboxFilter.posts ? "posts," : ""}${
        checkboxFilter.tags ? "tags" : ""
      }`;
      setSearchString(querystring);
      navigate(`?page=${currentPageNr - 1}${querystring}`, {
        replace: true,
      });
    }
  };

  return (
    <>
      <Layout
        title="Sök"
        description="Sök efter nyheter, bloggar, artister, band, trubaduer m.m. Hitta dina nya lokala favoriter"
      >
        <MainContainer>
          {/* Search Head */}
          <div className="mb-10">
            <ContentContainer>
              <form className="space-y-4 bg-[#131313] border border-white/10 rounded-2xl p-6 shadow-md">
                <h1 className="text-xl font-extrabold text-white uppercase tracking-wider pl-1">Sök</h1>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <input
                    name="q"
                    type="text"
                    placeholder="Sök"
                    className="input input-bordered bg-[#181818] border-white/10 text-stone-200 focus:border-[#0047ff] rounded-xl w-full md:w-96"
                    value={searchWord}
                    onChange={(e) => setSearchWord(e.target.value)}
                  />
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="form-control">
                      <label className="label cursor-pointer flex items-center gap-2 py-1">
                        <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Artister</span>
                        <input
                          name="bands"
                          type="checkbox"
                          defaultChecked={checkboxFilter.bands}
                          className="checkbox border-white/20 checked:bg-[#0047ff] checked:border-[#0047ff] rounded"
                          onChange={handleCheckboxChange}
                        />
                      </label>
                    </div>
                    <div className="form-control">
                      <label className="label cursor-pointer flex items-center gap-2 py-1">
                        <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Inlägg</span>
                        <input
                          name="posts"
                          type="checkbox"
                          defaultChecked={checkboxFilter.posts}
                          className="checkbox border-white/20 checked:bg-[#0047ff] checked:border-[#0047ff] rounded"
                          onChange={handleCheckboxChange}
                        />
                      </label>
                    </div>
                    <div className="form-control">
                      <label className="label cursor-pointer flex items-center gap-2 py-1">
                        <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Taggar</span>
                        <input
                          name="tags"
                          type="checkbox"
                          defaultChecked={checkboxFilter.tags}
                          className="checkbox border-white/20 checked:bg-[#0047ff] checked:border-[#0047ff] rounded"
                          onChange={handleCheckboxChange}
                        />
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={!enabledSearchBtn}
                    onClick={handleSearchSubmit}
                    className="btn bg-[#0047ff] hover:bg-blue-700 text-white border-none rounded-full font-bold uppercase tracking-wider text-xs px-6 py-3.5 transition-transform duration-300 hover:scale-[1.02] disabled:opacity-30 md:ml-auto"
                  >
                    Sök
                  </button>
                </div>
              </form>
              {errorMsg && (
                <p className="text-error font-semibold text-xs pl-1 mt-2">{errorMsg}</p>
              )}
            </ContentContainer>
          </div>

          {/* Results */}
          <ContentContainer>
            <div className="mb-4 pl-1">
              <h2 className="font-extrabold text-lg sm:text-xl text-white uppercase tracking-wide">
                Sökresultat {searchWordResults && `för ${searchWordResults}`}
              </h2>
            </div>

            {data && (
              <>
                {data.results.length === 0 ? (
                  <p className="text-stone-400 text-sm pl-1">Inga träffar</p>
                ) : (
                  <>
                    {/* Search results */}
                    <div className="flex flex-col gap-1">
                      {data.results.map((result, index) => (
                        <div key={index}>
                          <SearchResult data={result} />
                        </div>
                      ))}
                    </div>
                    {/* Pagnation btns */}
                    <div className="w-full flex justify-center my-10">
                      <div className="join shadow-md">
                        <button
                          className="join-item btn bg-[#131313] border border-white/10 text-stone-300 hover:bg-neutral-800 disabled:opacity-30 rounded-l-full px-4 font-bold"
                          onClick={goToPreviousPage}
                          disabled={currentPageNr === 1}
                        >
                          «
                        </button>
                        <button className="join-item btn bg-[#131313] border-y border-white/10 border-x-0 text-stone-300 pointer-events-none font-bold">
                          Sida {currentPageNr}{" "}
                          <span className="text-stone-500 font-semibold ml-1">
                            av {data.paginator.total_pages}
                          </span>
                        </button>
                        <button
                          className="join-item btn bg-[#131313] border border-white/10 text-stone-300 hover:bg-neutral-800 disabled:opacity-30 rounded-r-full px-4 font-bold"
                          onClick={goToNextPage}
                          disabled={
                            currentPageNr === data.paginator.total_pages
                          }
                        >
                          »
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </ContentContainer>
        </MainContainer>
      </Layout>
    </>
  );
};

export default SearchPage;
